/**
 * Shipping Rate Calculator Component
 * Handles shipping rate estimation in the cart drawer
 */

class ShippingEstimateCalculator {
  constructor(container) {
    this.container = container;
    if (!this.container) return;

    this.selectors = {
      form: '[data-shipping-estimate-form]',
      country: '[data-shipping-country]',
      province: '[data-shipping-province]',
      zip: '[data-shipping-zip]',
      submitBtn: '[data-shipping-estimate-submit]',
      results: '[data-shipping-estimate-results]'
    };

    this.elements = this.getElements();
    this.bindEvents();
  }

  getElements() {
    return {
      form: this.container.querySelector(this.selectors.form),
      country: this.container.querySelector(this.selectors.country),
      province: this.container.querySelector(this.selectors.province),
      zip: this.container.querySelector(this.selectors.zip),
      submitBtn: this.container.querySelector(this.selectors.submitBtn),
      results: this.container.querySelector(this.selectors.results)
    };
  }

  bindEvents() {
    if (this.elements.submitBtn) {
      this.elements.submitBtn.addEventListener('click', this.calculateShipping.bind(this));
    }
  }

  calculateShipping(e) {
    e.preventDefault();

    const { country, province, zip, results, submitBtn } = this.elements;
    if (!results) return;

    const zipValue = zip?.value?.trim() || '';
    const countryValue = country?.value || '';
    const provinceValue = province?.value || '';

    // Validate inputs
    if (!countryValue) {
      this.showError(results, window.shippingEstimateStrings?.selectCountry || 'Please select a country');
      return;
    }

    if (!zipValue) {
      this.showError(results, window.shippingEstimateStrings?.enterZip || 'Please enter a postal/zip code');
      return;
    }

    // Show loading state
    if (submitBtn) {
      submitBtn.classList.add('btn--loading');
      submitBtn.disabled = true;
    }
    results.style.display = 'block';
    results.innerHTML = '<p class="text-muted mb-0">' + (window.shippingEstimateStrings?.calculating || 'Calculating...') + '</p>';
    results.classList.remove('text-danger', 'text-success');

    // Build URL with encoded parameters
    const params = new URLSearchParams({
      'shipping_address[zip]': zipValue,
      'shipping_address[country]': countryValue,
      'shipping_address[province]': provinceValue
    });

    fetch(`/cart/shipping_rates.json?${params.toString()}`)
      .then(response => response.json())
      .then(data => {
        this.handleResponse(data, results);
      })
      .catch(error => {
        console.error('Shipping estimate error:', error);
        this.showError(results, window.shippingEstimateStrings?.genericError || 'Unable to calculate shipping rates. Please try again.');
      })
      .finally(() => {
        if (submitBtn) {
          submitBtn.classList.remove('btn--loading');
          submitBtn.disabled = false;
        }
      });
  }

  handleResponse(data, resultsContainer) {
    resultsContainer.innerHTML = '';

    if (data && data.shipping_rates) {
      const { shipping_rates } = data;

      if (shipping_rates.length > 0) {
        // Show success message with count
        const countText = (window.shippingEstimateStrings?.ratesFound || 'We found {{count}} shipping rate(s) for your address:')
          .replace('{{count}}', shipping_rates.length);
        
        const header = document.createElement('p');
        header.className = 'fw-medium mb-0 fs-md';
        header.textContent = countText;
        resultsContainer.appendChild(header);

        // Show each shipping rate
        shipping_rates.forEach(rate => {
          const rateItem = document.createElement('p');
          rateItem.className = 'fw-medium mb-0 fs-md';
          rateItem.innerHTML = `${this.escapeHtml(rate.name)}: <strong>${this.formatMoney(rate.price)}</strong>`;
          resultsContainer.appendChild(rateItem);
        });

        resultsContainer.classList.remove('text-danger');
        resultsContainer.classList.add('text-success');
      } else {
        this.showError(resultsContainer, window.shippingEstimateStrings?.noRates || 'No shipping rates available for this address.');
      }
    } else if (data) {
      // Handle error responses from API
      const errors = [];
      Object.entries(data).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          messages.forEach(msg => errors.push(msg));
        } else if (typeof messages === 'string') {
          errors.push(messages);
        }
      });

      if (errors.length > 0) {
        this.showError(resultsContainer, errors.join('<br>'));
      } else {
        this.showError(resultsContainer, window.shippingEstimateStrings?.genericError || 'Unable to calculate shipping rates.');
      }
    }
  }

  showError(container, message) {
    container.style.display = 'block';
    container.classList.remove('text-success');
    container.classList.add('text-danger');
    container.innerHTML = `<p class="mb-0">${message}</p>`;
  }

  formatMoney(cents) {
    // Convert cents to dollars and format
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }
    
    // Use theme's Utility.formatMoney if available
    if (typeof Utility !== 'undefined' && typeof Utility.formatMoney === 'function') {
      return Utility.formatMoney(cents);
    }

    // Use Shopify's formatMoney if available
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }

    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    const moneyFormat = window.theme?.moneyFormat || window.Shopify?.money_format || '${{amount}}';
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize all shipping estimate calculators on the page
function initShippingEstimateCalculators() {
  document.querySelectorAll('[data-cart-shipping-estimate]').forEach(container => {
    if (!container.hasAttribute('data-initialized')) {
      new ShippingEstimateCalculator(container);
      container.setAttribute('data-initialized', 'true');
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingEstimateCalculators);
} else {
  initShippingEstimateCalculators();
}

// Re-initialize when cart drawer opens (for AJAX loaded content)
if (typeof eventBus !== 'undefined' && typeof THEME_EVENTS !== 'undefined') {
  eventBus.subscribe(THEME_EVENTS.cartUpdate, initShippingEstimateCalculators);
}

// Export for manual initialization
window.ShippingEstimateCalculator = ShippingEstimateCalculator;
window.initShippingEstimateCalculators = initShippingEstimateCalculators;
